import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { CHANGELOG, APP_VERSION, type ChangeType } from '@/lib/changelog'
import { cn } from '@/lib/utils'

const BADGE: Record<ChangeType, { label: string; className: string }> = {
  feature: {
    label: 'new',
    className: 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/60',
  },
  fix: {
    label: 'fix',
    className: 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-950/60',
  },
  optimization: {
    label: 'opt',
    className: 'text-sky-700 bg-sky-100 dark:text-sky-300 dark:bg-sky-950/60',
  },
}

function ChangelogBody() {
  return (
    <div className="space-y-7">
      {CHANGELOG.map((entry, idx) => (
        <div key={entry.version}>
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="font-mono text-sm font-semibold">v{entry.version}</span>
            {idx === 0 && (
              <span className="font-mono text-[10px] px-1 rounded bg-secondary text-secondary-foreground">
                current
              </span>
            )}
            <span className="text-[11px] text-muted-foreground ml-auto">{entry.date}</span>
          </div>
          <p className="text-sm font-medium mb-2">{entry.title}</p>
          <ul className="space-y-1.5">
            {entry.changes.map((change, i) => (
              <li key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                <span
                  className={cn(
                    'font-mono text-[10px] px-1 py-px rounded flex-shrink-0 mt-0.5 w-8 text-center',
                    BADGE[change.type].className,
                  )}
                >
                  {BADGE[change.type].label}
                </span>
                <span className="text-muted-foreground flex-1">{change.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

/**
 * Slim version footer. Tapping the version opens the release notes — a side
 * sheet on desktop, a bottom sheet on mobile (same surfaces as Settings).
 */
export function AppFooter({ className }: { className?: string }) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  return (
    <footer className={cn('flex-shrink-0 border-t border-border/40 pb-[var(--sab)]', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-7 flex items-center justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="font-mono text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors px-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Version ${APP_VERSION} — view release notes`}
        >
          v{APP_VERSION}
        </button>
      </div>

      {isMobile ? (
        <BottomSheet open={open} onOpenChange={setOpen} title="Release notes">
          <div className="px-5 pb-8">
            <ChangelogBody />
          </div>
        </BottomSheet>
      ) : (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="w-full sm:max-w-sm shadow-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Release notes</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <ChangelogBody />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </footer>
  )
}
